import { useState, useEffect } from "react";
import { brandService } from "../../services/brand.service";
import { formatCurrency } from "../../utils/formatters";
import { format } from "date-fns";
import PageHeader from "../../components/common/PageHeader";
import { Pagination } from "../../components/common/Pagination";
import { toast } from "react-hot-toast";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { EmptyState } from "../../components/ui/empty-state";
import { DollarSign, ShoppingCart, TrendingUp } from "lucide-react";

interface PostCheckoutSale {
  id: string;
  orderId: string;
  orderNumber: string;
  orderStatusUrl: string | null;
  productTitle: string | null;
  totalPrice: number;
  commission: number;
  commissionType: string;
  commissionValue: string;
  discountCode: string;
  status: string;
  createdAt: string;
  affiliate: {
    id: string;
    name: string;
    email: string;
  };
  isMigrated: boolean;
}

interface PostCheckoutSalesData {
  sales: PostCheckoutSale[];
  stats: {
    totalSales: number;
    totalCommissions: number;
    totalOrders: number;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export default function PostCheckoutSales() {
  const [data, setData] = useState<PostCheckoutSalesData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    fetchSales();
  }, [currentPage]);

  const fetchSales = async () => {
    try {
      setIsLoading(true);
      const response = await brandService.getPostCheckoutSales(currentPage, limit);
      setData(response);
    } catch (error) {
      console.error("Failed to fetch post-checkout sales:", error);
      toast.error("Failed to load post-checkout sales");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !data) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Post-Checkout Sales"
          description="View sales from affiliates created via post-checkout flow"
        />
        <div className="animate-pulse space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card p-6 rounded-lg h-32" />
            ))}
          </div>
          <div className="bg-card p-6 rounded-lg h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Post-Checkout Sales"
        description="View sales from affiliates created via post-checkout flow"
      />

      {/* Stats Cards */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(data.stats.totalSales)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                From {data.stats.totalOrders} orders
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Commissions</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(data.stats.totalCommissions)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Earned by affiliates
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{data.stats.totalOrders}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Post-checkout sales
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sales Table */}
      <Card>
        <CardHeader>
          <CardTitle>Sales History</CardTitle>
        </CardHeader>
        <CardContent>
          {data && data.sales.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Affiliate</TableHead>
                      <TableHead>Discount Code</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Commission</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.sales.map((sale) => (
                      <TableRow key={sale.id}>
                        <TableCell className="font-medium">
                          <span className="text-sm">#{sale.orderNumber}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{sale.affiliate.name}</span>
                            <span className="text-xs text-muted-foreground">{sale.affiliate.email}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="bg-muted px-2 py-1 rounded text-xs">
                            {sale.discountCode}
                          </code>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(sale.totalPrice)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-col items-end">
                            <span className="font-medium">{formatCurrency(sale.commission)}</span>
                            <span className="text-xs text-muted-foreground">
                              {sale.commissionType === "PERCENTAGE" 
                                ? `${sale.commissionValue}%` 
                                : `$${sale.commissionValue}`}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(new Date(sale.createdAt), "MMM d, yyyy")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {data.pagination.totalPages > 1 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={data.pagination.totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          ) : (
            <EmptyState
              bordered={false}
              icon={<ShoppingCart />}
              title="No post-checkout sales"
              body="Sales from post-checkout affiliates will appear here"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

